import { DataAction, Payload, Persistence, StateRepository } from '@angular-ru/ngxs/decorators';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { computed, Injectable, signal } from '@angular/core';
import { Selector, State, Store } from '@ngxs/store';
import { produce } from 'immer';
import { BehaviorSubject } from 'rxjs';
import { MediaService } from './../shared/layouts/main/media.service';

export type MainMenuBigSideMode = Extract<MainMenuMode, MainMenuMode.SIDE | MainMenuMode.PUSH>;
export enum MainMenuMode {
    OVER = 'over',
    SIDE = 'side',
    PUSH = 'push',
}

export enum MainMenuSize {
    EXPANDED = 'expanded',
    COMPACT = 'compact',
    COLLAPSED = 'collapsed',
}

@Persistence([
    {
        path: 'layoutState.mainMenu',
        existingEngine: sessionStorage
    }
])
@StateRepository()
@State<LayoutStateModel>({
    name: 'layoutState',
    defaults: {
        mainMenu: {
            mainMenuSize: MainMenuSize.EXPANDED,
            bigSizeMode: MainMenuMode.SIDE
        }
    }
})
@Injectable()
export class LayoutService extends NgxsDataRepository<LayoutStateModel> {

    currentLayout$ = new BehaviorSubject<any>(null);
    // currentLayout$ = new BehaviorSubject<MainLayoutComponent>(null);


    @Selector()
    static rightPanelActive(state: LayoutStateModel) {
        return state.rightPanelHoverActive;
    }
    @Selector()
    static overlayRightPanelActive(state: LayoutStateModel) {
        return state.rightPanelHoverActive;
    }
    @Selector()
    static staticRightPanelMobileActive(state: LayoutStateModel) {
        return state.rightPanelHoverActive;
    }
    @Selector()
    static resetRightPanel(state: LayoutStateModel) {
        return state.rightPanelHoverActive;
    }

    @Selector()
    static rightPanelClick(state: LayoutStateModel) {
        return state.rightPanelHoverActive;
    }
    @Selector()
    static rightPanelHoverActive(state: LayoutStateModel) {
        return state.rightPanelHoverActive;
    }


    get rightPanelClickSync(): boolean {
        return this.store.selectSnapshot<boolean>(LayoutService.rightPanelClick);
    }
    get rightPanelClick$() {
        return this.store.select<boolean>(LayoutService.rightPanelClick);
    }

    get rightPanelActiveSync(): boolean {
        return this.store.selectSnapshot<boolean>(LayoutService.rightPanelActive);
    }
    get rightPanelActive$() {
        return this.store.select<boolean>(LayoutService.rightPanelActive);
    }

    get overlayRightPanelActiveSync(): boolean {
        return this.store.selectSnapshot<boolean>(LayoutService.overlayRightPanelActive);
    }
    get overlayRightPanelActive$() {
        return this.store.select<boolean>(LayoutService.overlayRightPanelActive);
    }

    get staticRightPanelMobileActiveSync(): boolean {
        return this.store.selectSnapshot<boolean>(LayoutService.staticRightPanelMobileActive);
    }
    get staticRightPanelMobileActive$() {
        return this.store.select<boolean>(LayoutService.staticRightPanelMobileActive);
    }

    get resetRightPanelSync(): boolean {
        return this.store.selectSnapshot<boolean>(LayoutService.resetRightPanel);
    }
    get resetRightPanel$() {
        return this.store.select<boolean>(LayoutService.resetRightPanel);
    }

    get rightPanelHoverActiveSync(): boolean {
        return this.store.selectSnapshot<boolean>(LayoutService.rightPanelHoverActive);
    }
    get rightPanelHoverActive$() {
        return this.store.select<boolean>(LayoutService.rightPanelHoverActive);
    }


    constructor(private readonly store: Store, private readonly mediaService: MediaService) {
        super();
        this.store.select(LayoutService.mainMenuSizeSelector).subscribe((value) => {
            console.log('mainMenuSizeSelector', value);
            this.mainMenuSize.set(value);
        });
    }


    @DataAction()
    setRightPanelActive(@Payload('value') value: boolean) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: LayoutStateModel) => {
            draft.rightPanelActive = value;
        }))
    }

    @DataAction()
    toggleRightPanelActive() {
        const rightPanel = this.snapshot.rightPanelActive;
        this.ctx.setState(produce(this.ctx.getState(), (draft: LayoutStateModel) => {
            draft.rightPanelActive = !rightPanel;
        }))
    }



    @DataAction()
    setOverlayRightPanelActive(@Payload('value') value: boolean) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: LayoutStateModel) => {
            draft.overlayRightPanelActive = value;
        }))
    }
    @DataAction()
    setStaticRightPanelMobileActive(@Payload('value') value: boolean) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: LayoutStateModel) => {
            draft.staticRightPanelMobileActive = value;
        }))
    }
    @DataAction()
    setResetRightPanel(@Payload('value') value: boolean) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: LayoutStateModel) => {
            draft.resetRightPanel = value;
        }))
    }

    @DataAction()
    setRightPanelClick(@Payload('value') value: boolean) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: LayoutStateModel) => {
            draft.rightPanelClick = value;
        }))
    }
    @DataAction()
    setRightPanelHoverActive(@Payload('value') value: boolean) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: LayoutStateModel) => {
            draft.rightPanelHoverActive = value;
        }))
    }

    // Application Main menu
    @Selector()
    static mainMenuSizeSelector(state: LayoutStateModel) {
        return state.mainMenu?.mainMenuSize;
    }
    mainMenuSize = signal<MainMenuSize>(MainMenuSize.EXPANDED);
    bigSideMode = signal<MainMenuBigSideMode>(MainMenuMode.SIDE);

    @DataAction()
    setMainMenuSize(size: MainMenuSize) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: LayoutStateModel) => {
            draft.mainMenu.mainMenuSize = size;
        }));
    }

    calculateMainMenuMode() {
        if (this.mediaService.isLESmall()) return MainMenuMode.OVER;
        else return this.bigSideMode();
    }
    calculateMainMenuSize() {
        const menuSize = this.mainMenuSize();
        const isSmall = this.mediaService.isLESmall();

        if (menuSize == MainMenuSize.COLLAPSED) return MainMenuSize.COLLAPSED;
        else if (isSmall) return MainMenuSize.EXPANDED;
        else return menuSize;
    }

    mainManuContainerClasses = computed(() => {
        this.mediaService.isLESmall();
        this.mainMenuSize();

        return {
            ['main-menu-' + this.calculateMainMenuMode().toLowerCase()]: true,
            ['main-menu-' + this.calculateMainMenuSize().toLowerCase()]: true,
        }
    });
}





export interface LayoutStateModel {
    rightPanelActive?: boolean;
    overlayRightPanelActive?: boolean;
    staticRightPanelMobileActive?: boolean;
    resetRightPanel?: boolean;

    rightPanelClick?: boolean;
    rightPanelHoverActive?: boolean;

    mainMenu?: MainMenuState;

}

export interface MainMenuState {
    mainMenuSize?: MainMenuSize;
    bigSizeMode?: MainMenuBigSideMode;
}
